import graphql from "babel-plugin-relay/macro";
import { useCallback } from "react";

import { useMutationWithError } from "@/utils/hooks/useMutationWithError";

import {
  useClaimSlotForRequestMutation,
  useClaimSlotForRequestMutation$data,
} from "./__generated__/useClaimSlotForRequestMutation.graphql";

type ClaimSlotForRequestInput = {
  requestBookingId: string;
  targetSlotId: string;
};

export const useClaimSlotForRequest = () => {
  const [claimSlotForRequestMutation, isClaimingSlotForRequest] =
    useMutationWithError<useClaimSlotForRequestMutation>(
      useClaimSlotForRequestMutationString
    );

  const claimSlotForRequest = useCallback(
    (
      input: ClaimSlotForRequestInput,
      onCompleted?: (result: {
        success: boolean;
        draftBookingId?: string | null;
        targetSlot?: any;
        errors?: { code: string; field?: string | null }[];
      }) => void,
      onError?: (error: Error) => void
    ) => {
      claimSlotForRequestMutation({
        variables: {
          input,
        },
        onCompleted: (response: useClaimSlotForRequestMutation$data) => {
          const payload = response?.responsePayload;
          if (payload?.success) {
            onCompleted?.({
              success: true,
              draftBookingId: payload.draftBooking?.id,
              targetSlot: payload.targetSlot,
              errors:
                payload.errors?.map((error) => ({
                  code: error?.code ?? "",
                  field: error?.field?.[0] ?? null,
                })) || [],
            });
          } else {
            onCompleted?.({
              success: false,
              draftBookingId: null,
              targetSlot: null,
              errors:
                payload?.errors?.map((error) => ({
                  code: error?.code ?? "",
                  field: error?.field?.[0] ?? null,
                })) || [],
            });
          }
        },
        onErrorCustom: (err: Error) => {
          onError?.(err);
        },
      });
    },
    [claimSlotForRequestMutation]
  );

  return [claimSlotForRequest, isClaimingSlotForRequest] as const;
};

const useClaimSlotForRequestMutationString = graphql`
  mutation useClaimSlotForRequestMutation($input: ClaimSlotForRequestInput!) {
    responsePayload: claimSlotForRequest(input: $input) {
      success
      errors {
        code

        message
        field
      }
      draftBooking {
        id
      }
      targetSlot {
        ... on TimeSlot {
          id
          lockedUntil
          availability
          isLocked
        }
      }
    }
  }
`;
